import { buildQueryParamLink } from 'lib/helpers/urlHelper';
import { ISwrConfiguration, useFetch } from 'lib/hooks/useFetch';
import { QuestionType } from 'lib/types/nudge';
import _ from 'lodash';
import {
  ICmsCompanyCourseVariableValues,
  ICmsCourseVariable,
  IHydratedQuestion,
} from '../types';
import { DEFAULT_CONTENT_VARIABLES } from './datasets';
import { IDocxExportNudge } from './DocxExporter/types';
import { ICmsNudgeGroup, IInternalVideoBundle } from './types';

/** Course-specific content variables. */
export const useCmsCourseVariables = (
  courseId: number,
  options?: ISwrConfiguration
) =>
  useFetch<ICmsCourseVariable[]>(
    `/internal/cms/courses/${courseId}/course-variables/`,
    options
  );

/** Course-specific, company-specific content variables. */
export const useCmsCompanyCourseVariables = (
  courseId: number,
  companyId: number,
  language: string,
  options?: ISwrConfiguration
) => {
  const shouldFetch = !!companyId;
  return useFetch<ICmsCompanyCourseVariableValues>(
    buildQueryParamLink(
      `/internal/cms/courses/${courseId}/course-variables/company/${companyId}`,
      { language }
    ),
    { ...options, shouldFetch }
  );
};

/** Aggregate of the above hooks: course- and company-specific content variables. */
export const useCmsCourseCompanyRenderVariables = (params: {
  courseId: number;
  companyId: number;
  companyName: string;
  language: string;
}): {
  courseVariables: ICmsCourseVariable[];
  companyCmsCourseVariables: ICmsCompanyCourseVariableValues;
  variablesToRender: { [variableName: string]: string };
} => {
  const { data: courseVariables } = useCmsCourseVariables(params.courseId);
  const { data: companyCmsCourseVariables } = useCmsCompanyCourseVariables(
    params.courseId,
    params.companyId,
    params.language
  );

  const companyVariableValues = companyCmsCourseVariables?.variableValues;

  const customContentVariables = _.chain(courseVariables)
    .map((courseVar) => {
      const companyValue = _.find(
        companyVariableValues,
        (val) => val.variableId === courseVar.id
      );
      return [courseVar.variableName, companyValue?.variableValue];
    })
    .fromPairs()
    .value();

  return {
    courseVariables,
    companyCmsCourseVariables,
    variablesToRender: _.extend(
      DEFAULT_CONTENT_VARIABLES,
      {
        companyName:
          params.companyName || DEFAULT_CONTENT_VARIABLES.companyName,
      },
      customContentVariables
    ),
  };
};

export const useCmsQuestion = (
  questionId: number,
  options?: ISwrConfiguration
) =>
  useFetch<IHydratedQuestion>(`/internal/cms/question/${questionId}`, options);

export const useCmsQuestions = (
  params?: {
    companyIds?: number | number[];
    searchTerm?: string;
    types?: QuestionType | QuestionType[];
  },
  options?: ISwrConfiguration
) => {
  return useFetch<IHydratedQuestion[]>(
    buildQueryParamLink('/internal/cms/questions', params),
    options
  );
};

export const useCmsNudgeGroup = (
  nudgeGroupId: number,
  options?: ISwrConfiguration
) => useFetch<ICmsNudgeGroup>(`/internal/cms/groups/${nudgeGroupId}`, options);

export const useDocxExportNudge = (
  nudgeId: number,
  options?: ISwrConfiguration
) =>
  useFetch<IDocxExportNudge>(
    `/internal/cms/nudges/${nudgeId}/docx-export`,
    options
  );

export const useSearchLiveSlides = (
  term: string,
  options?: ISwrConfiguration
) =>
  useFetch<ICmsNudgeGroup[]>(
    buildQueryParamLink('/internal/cms/nudges/search', { term }),
    options
  );

export const useSelfHostedVideos = (options?: ISwrConfiguration) =>
  useFetch<IInternalVideoBundle[]>(`/internal/videos`, options);

export const useSelfHostedVideo = (
  videoId: number,
  options?: ISwrConfiguration
) => useFetch<IInternalVideoBundle>(`/internal/videos/${videoId}`, options);
